/**
 * Where a funscript is at a given moment: the position (0-100) between the two actions around `timeMs`.
 * Used to put the device where the timeline is while the user scrubs with nothing playing.
 *
 * `actions` need not be sorted. Before the first action it returns the first position, after the last
 * action the last position, and null for an empty script.
 */
export function positionAtTime(actions: ReadonlyArray<{ at: number; pos: number }>, timeMs: number): number | null {
  const n = actions.length
  if (n === 0) return null

  let sorted = actions
  for (let i = 1; i < n; i++) {
    if (actions[i].at < actions[i - 1].at) {
      sorted = [...actions].sort((a, b) => a.at - b.at)
      break
    }
  }

  if (timeMs <= sorted[0].at) return clamp(sorted[0].pos)
  const last = sorted[n - 1]
  if (timeMs >= last.at) return clamp(last.pos)

  // Binary search for the last action at or before timeMs.
  let lo = 0
  let hi = n - 1
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (sorted[mid].at <= timeMs) lo = mid
    else hi = mid - 1
  }
  const a = sorted[lo]
  const b = sorted[lo + 1]
  const span = b.at - a.at
  if (span <= 0) return clamp(a.pos)
  const t = (timeMs - a.at) / span
  return clamp(Math.round(a.pos + (b.pos - a.pos) * t))
}

function clamp(pos: number): number {
  return Math.max(0, Math.min(100, pos))
}
